import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  CloseButton,
  Flex,
  HStack,
  Spacer,
  Spinner,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import { format } from "date-fns";
import {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  graphql,
  usePaginationFragment,
  useRelayEnvironment,
} from "react-relay";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { fetchQuery } from "relay-runtime";

import { PopupGroupOrder, UserRole } from "~/src/__generated__/schema";
import { PopupListPage_site$key } from "~/src/__relay_artifacts__/PopupListPage_site.graphql";
import { PopupListPageAcknowledgeMutation } from "~/src/__relay_artifacts__/PopupListPageAcknowledgeMutation.graphql";
import { PopupListPageActiveJobQuery } from "~/src/__relay_artifacts__/PopupListPageActiveJobQuery.graphql";
import { PopupListPageJobStatusQuery } from "~/src/__relay_artifacts__/PopupListPageJobStatusQuery.graphql";
import { PopupListPageLatestJobQuery } from "~/src/__relay_artifacts__/PopupListPageLatestJobQuery.graphql";
import { OutlineButton, SolidButton } from "~/src/components/common/Button";
import {
  Table,
  TableHeaderCheckboxColumn,
  TableHeaderColumn,
  TableHeaderRow,
} from "~/src/components/common/tables/Table";
import { useTableItemSelect } from "~/src/components/common/tables/TableItemSelect";
import {
  TablePagination,
  useTablePaginationProps,
} from "~/src/components/common/tables/TablePagination";
import { TableSearchField } from "~/src/components/common/tables/TableSearchField";
import { ListFilterFormValues as FormValues } from "~/src/components/features/filter";
import { PageLayout } from "~/src/components/features/global/PageLayout";
import { TableItemSelectButton } from "~/src/components/features/global/TableItemSelectButton";
import { PopupCsvUploadModal } from "~/src/components/features/popup/PopupImport/presentations";
import { useBulkCheckboxes } from "~/src/lib/hooks";
import { useMutationCommit } from "~/src/lib/react-relay";
import { useUpdateDebounce } from "~/src/lib/react-use";
import { downloadCsvWithBom } from "~/src/lib/utils";

import { allTableItems, initialSelectedTableItems } from "./constants";
import { PopupBulkOperaionSelect } from "./PopupBulkOperationSelect";
import { PopupFilterButton } from "./PopupFilterButton";
import { PopupTableDataRow } from "./PopupTableDataRow";

const activeJobQuery = graphql`
  query PopupListPageActiveJobQuery {
    activePopupImportJob {
      slug
      status
      totalCount
      succeededCount
      failedCount
      retryCsv
      errorMessage
    }
  }
`;

const jobStatusQuery = graphql`
  query PopupListPageJobStatusQuery($slug: String!) {
    popupImportJob(slug: $slug) {
      slug
      status
      totalCount
      succeededCount
      failedCount
      retryCsv
      errorMessage
    }
  }
`;

const latestJobQuery = graphql`
  query PopupListPageLatestJobQuery {
    latestPopupImportJob {
      slug
      status
      totalCount
      succeededCount
      failedCount
      retryCsv
      errorMessage
    }
  }
`;

const acknowledgeMutation = graphql`
  mutation PopupListPageAcknowledgeMutation(
    $input: AcknowledgePopupImportJobInput!
  ) {
    acknowledgePopupImportJob(input: $input) {
      popupImportJob {
        slug
        status
      }
    }
  }
`;

type ImportJobSnapshot = {
  slug: string;
  status: string;
  totalCount: number;
  succeededCount: number;
  failedCount: number;
  retryCsv: string | null;
  errorMessage: string | null;
};

const ACTIVE_STATUSES = new Set(["QUEUED", "RUNNING"]);
const TERMINAL_STATUSES = new Set([
  "COMPLETED",
  "COMPLETED_WITH_ERRORS",
  "FAILED",
]);

const POLL_INTERVAL_MS = 3000;

export type Props = {
  role: UserRole;
  siteRef: PopupListPage_site$key;
  filterValues: FormValues;
  setFilterValues: Dispatch<SetStateAction<FormValues>>;
};

type PopupListLocationState = {
  refetchPopupList?: boolean;
  importJobSlug?: string;
};

const greenOutlineSx = {
  color: "#38A169",
  borderColor: "#38A169",
  _hover: { color: "#2F855A", borderColor: "#2F855A" },
  _active: { color: "#276749", borderColor: "#276749" },
};

const siteFragment = graphql`
  fragment PopupListPage_site on Site
  @refetchable(queryName: "PopupListPage_Query")
  @argumentDefinitions(
    count: { type: "Int", defaultValue: 120 }
    cursor: { type: "String" }
    searchTerm: { type: "String" }
    pageIds: { type: "[ID!]" }
    funnelIds: { type: "[ID!]" }
    pageKind: { type: "PageKind" }
    sourceId: { type: "ID" }
    device: { type: "Device" }
    withArchived: { type: "Boolean" }
    status: { type: "DistributionStatus" }
    order: {
      type: "PopupGroupOrder"
      defaultValue: { field: NAME, direction: ASC }
    }
  ) {
    slug
    popupGroups(
      first: $count
      after: $cursor
      searchTerm: $searchTerm
      pageIds: $pageIds
      funnelIds: $funnelIds
      sourceId: $sourceId
      device: $device
      withArchived: $withArchived
      orderBy: $order
      status: $status
      pageKind: $pageKind
    ) @connection(key: "PopupListPage_popupGroups") {
      __id
      totalCount
      edges {
        node {
          slug
          ...PopupTableDataRow_popupGroup
        }
      }
    }
    popupCsv(
      searchText: $searchTerm
      orderBy: $order
      withArchived: $withArchived
      pageKind: $pageKind
      pageIds: $pageIds
      funnelIds: $funnelIds
      sourceId: $sourceId
      device: $device
      status: $status
    )
  }
`;

export const PopupListPage: FC<
  React.PropsWithChildren<React.PropsWithChildren<Props>>
> = ({ siteRef, role, filterValues, setFilterValues }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const environment = useRelayEnvironment();
  const [searchText, setSearchText] = useState("");
  const [sort, setSort] = useState<PopupGroupOrder>({
    field: "NAME",
    direction: "DESC",
  });

  const [importJob, setImportJob] = useState<ImportJobSnapshot | null>(null);
  const [isBannerDismissed, setIsBannerDismissed] = useState(false);
  const importJobSlugRef = useRef<string | null>(null);
  const pollingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const acknowledge =
    useMutationCommit<PopupListPageAcknowledgeMutation>(acknowledgeMutation);

  const {
    data: site,
    loadNext,
    hasNext,
    refetch,
  } = usePaginationFragment(siteFragment, siteRef);

  const {
    isOpen: isCsvImportOpen,
    onOpen: onCsvImportOpen,
    onClose: onCsvImportClose,
  } = useDisclosure();

  const { tablePaginationProps } = useTablePaginationProps({
    totalCount: site.popupGroups.totalCount,
    hasNext,
    loadNext,
    refetch,
  });

  const refetchList = useCallback(() => {
    void refetch({
      searchTerm: searchText,
      count: tablePaginationProps.perPage,
      order: {
        field: sort.field,
        direction: sort.direction,
      },
      ...filterValues,
    });
  }, [
    filterValues,
    refetch,
    searchText,
    sort.direction,
    sort.field,
    tablePaginationProps.perPage,
  ]);

  useEffect(() => {
    const state = location.state as PopupListLocationState | null | undefined;
    if (!state?.refetchPopupList) return;
    refetchList();
    navigate(
      { pathname: location.pathname, search: location.search },
      { replace: true, state: {} }
    );
  }, [
    location.pathname,
    location.search,
    location.state,
    navigate,
    refetchList,
  ]);

  const startPolling = useCallback(
    (slug: string) => {
      importJobSlugRef.current = slug;

      const poll = () => {
        void fetchQuery<PopupListPageJobStatusQuery>(
          environment,
          jobStatusQuery,
          { slug },
          { fetchPolicy: "network-only" }
        )
          .toPromise()
          .then((data) => {
            const job = data?.popupImportJob;
            if (!job) return;

            const snapshot: ImportJobSnapshot = {
              slug: job.slug,
              status: job.status,
              totalCount: job.totalCount,
              succeededCount: job.succeededCount,
              failedCount: job.failedCount,
              retryCsv: job.retryCsv ?? null,
              errorMessage: job.errorMessage ?? null,
            };
            setImportJob(snapshot);

            if (
              importJobSlugRef.current === slug &&
              !TERMINAL_STATUSES.has(job.status)
            ) {
              pollingTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
            } else if (TERMINAL_STATUSES.has(job.status)) {
              if (
                job.status === "COMPLETED" ||
                job.status === "COMPLETED_WITH_ERRORS"
              ) {
                refetchList();
              }
            }
          })
          .catch(() => {
            if (importJobSlugRef.current === slug) {
              pollingTimerRef.current = setTimeout(poll, POLL_INTERVAL_MS * 2);
            }
          });
      };

      poll();
    },
    [environment, refetchList]
  );

  useEffect(() => {
    const state = location.state as PopupListLocationState | null | undefined;
    const slugFromState = state?.importJobSlug;

    if (slugFromState) {
      setIsBannerDismissed(false);
      setImportJob(null);
      navigate(
        { pathname: location.pathname, search: location.search },
        { replace: true, state: {} }
      );
      startPolling(slugFromState);
      return;
    }

    void fetchQuery<PopupListPageActiveJobQuery>(
      environment,
      activeJobQuery,
      {},
      { fetchPolicy: "network-only" }
    )
      .toPromise()
      .then((data) => {
        const job = data?.activePopupImportJob;
        if (job) {
          const snapshot: ImportJobSnapshot = {
            slug: job.slug,
            status: job.status,
            totalCount: job.totalCount,
            succeededCount: job.succeededCount,
            failedCount: job.failedCount,
            retryCsv: job.retryCsv ?? null,
            errorMessage: job.errorMessage ?? null,
          };
          setImportJob(snapshot);

          if (!TERMINAL_STATUSES.has(job.status)) {
            startPolling(job.slug);
          }
          return;
        }

        void fetchQuery<PopupListPageLatestJobQuery>(
          environment,
          latestJobQuery,
          {},
          { fetchPolicy: "network-only" }
        )
          .toPromise()
          .then((latestData) => {
            const latestJob = latestData?.latestPopupImportJob;
            if (!latestJob) return;

            const snapshot: ImportJobSnapshot = {
              slug: latestJob.slug,
              status: latestJob.status,
              totalCount: latestJob.totalCount,
              succeededCount: latestJob.succeededCount,
              failedCount: latestJob.failedCount,
              retryCsv: latestJob.retryCsv ?? null,
              errorMessage: latestJob.errorMessage ?? null,
            };
            setImportJob(snapshot);
          });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    return () => {
      importJobSlugRef.current = null;
      if (pollingTimerRef.current !== null) {
        clearTimeout(pollingTimerRef.current);
        pollingTimerRef.current = null;
      }
    };
  }, []);

  const handleDismissBanner = useCallback(() => {
    if (importJob && TERMINAL_STATUSES.has(importJob.status)) {
      void acknowledge({
        variables: { input: { slug: importJob.slug } },
      }).catch(() => {
        // Acknowledgement is best-effort; ignore failures on dismiss.
      });
    }
    setIsBannerDismissed(true);
    importJobSlugRef.current = null;
    if (pollingTimerRef.current !== null) {
      clearTimeout(pollingTimerRef.current);
      pollingTimerRef.current = null;
    }
  }, [acknowledge, importJob]);

  const handleRetryDownload = useCallback(() => {
    if (!importJob?.retryCsv) return;
    downloadCsvWithBom(
      `ポップアップインポート失敗行-${format(new Date(), "yyyy-MM-dd")}`,
      importJob.retryCsv
    );
    handleDismissBanner();
  }, [handleDismissBanner, importJob]);

  const isImportActive =
    importJob !== null && ACTIVE_STATUSES.has(importJob.status);

  const { selectedTableItems, dataColumns, onApplyClick } = useTableItemSelect({
    cacheKey: "PopupListPage",
    initialSelectedTableItems,
  });
  const handleSortClick = useCallback((params: any) => setSort(params), []);

  const popups = useMemo(() => {
    const from = tablePaginationProps.from;
    const to = tablePaginationProps.to;
    const egdes = site.popupGroups.edges || [];
    return egdes.slice(from, to).map((edge) => {
      const node = edge?.node;
      if (!node) throw new Error("assertion failed");
      return node;
    });
  }, [
    site.popupGroups.edges,
    tablePaginationProps.from,
    tablePaginationProps.to,
  ]);

  const {
    selectedSlugs,
    isAllChecked,
    handleAllCheckboxClick,
    handleCheckboxClick,
  } = useBulkCheckboxes(popups);

  useUpdateDebounce(
    () =>
      refetch({
        searchTerm: searchText,
        count: tablePaginationProps.perPage,
        order: {
          field: sort.field,
          direction: sort.direction,
        },
        ...filterValues,
      }),
    500,
    [filterValues, refetch, searchText, tablePaginationProps.perPage, sort]
  );

  const handleCsvDownload = useCallback(() => {
    const csvText = site.popupCsv?.data?.toString();
    if (!csvText) return;
    downloadCsvWithBom(
      `ポップアップ＆パターン登録状況一覧-${format(new Date(), "yyyy-MM-dd")}`,
      csvText
    );
  }, [site.popupCsv?.data]);

  const renderImportBanner = () => {
    if (!importJob || isBannerDismissed) return null;

    const { status, totalCount, succeededCount, failedCount, errorMessage } =
      importJob;

    if (status === "QUEUED" || status === "RUNNING") {
      return (
        <Alert status="info" mb="12px" borderRadius="4px">
          <Spinner size="xs" mr="8px" />
          <AlertDescription fontSize="13px" flex="1">
            ポップアップのインポートを処理中です…
            {totalCount > 0 && (
              <Text as="span" ml="8px" color="gray.600">
                ({succeededCount + failedCount} / {totalCount} 件処理済み)
              </Text>
            )}
          </AlertDescription>
          <CloseButton onClick={handleDismissBanner} />
        </Alert>
      );
    }

    if (status === "COMPLETED") {
      return (
        <Alert status="success" mb="12px" borderRadius="4px">
          <AlertIcon />
          <AlertDescription fontSize="13px" flex="1">
            インポートが完了しました（{succeededCount}件成功）
          </AlertDescription>
          <CloseButton onClick={handleDismissBanner} />
        </Alert>
      );
    }

    if (status === "COMPLETED_WITH_ERRORS") {
      return (
        <Alert status="warning" mb="12px" borderRadius="4px">
          <AlertIcon />
          <AlertDescription fontSize="13px" flex="1">
            インポートが完了しました（{succeededCount}件成功 / {failedCount}
            件失敗）
            {importJob.retryCsv && (
              <Button
                variant="link"
                size="sm"
                colorScheme="orange"
                ml="12px"
                onClick={handleRetryDownload}
              >
                リトライCSVをダウンロード
              </Button>
            )}
            {importJob.retryCsv && (
              <Text mt="4px" color="gray.600">
                ダウンロードしたCSVの「エラー」列で各行のエラー内容を確認し、修正のうえ再インポートしてください。
              </Text>
            )}
          </AlertDescription>
          <CloseButton onClick={handleDismissBanner} />
        </Alert>
      );
    }

    if (status === "FAILED") {
      return (
        <Alert status="error" mb="12px" borderRadius="4px">
          <AlertIcon />
          <AlertDescription fontSize="13px" flex="1">
            インポートが失敗しました
            {errorMessage && (
              <Text as="span" ml="8px" color="gray.600">
                ({errorMessage})
              </Text>
            )}
          </AlertDescription>
          <CloseButton onClick={handleDismissBanner} />
        </Alert>
      );
    }

    return null;
  };

  return (
    <PageLayout title="ポップアップ">
      <Box my="16px">
        {renderImportBanner()}
        <HStack justifyContent="flex-end" mb="16px" spacing="16px">
          <OutlineButton onClick={handleCsvDownload} sx={greenOutlineSx}>
            CSVダウンロード
          </OutlineButton>
          <Tooltip
            isDisabled={!isImportActive}
            label="インポート実行中です。完了後にアップロードできます。"
            placement="top"
          >
            <Box display="inline-block">
              <OutlineButton
                sx={greenOutlineSx}
                onClick={onCsvImportOpen}
                isDisabled={isImportActive}
              >
                CSVアップロード
              </OutlineButton>
            </Box>
          </Tooltip>
          <NavLink to={`/sites/${site.slug}/popup_groups/new`}>
            <SolidButton>登録</SolidButton>
          </NavLink>
        </HStack>
        <Flex alignItems="flex-end">
          <PopupBulkOperaionSelect selectedSlug={selectedSlugs} />
          <TableSearchField searchText={searchText} onChange={setSearchText} />
          <Spacer />
          <HStack spacing="12px">
            <PopupFilterButton
              siteSlug={site.slug}
              filterValues={filterValues}
              onFilterChange={setFilterValues}
              onSubmit={setFilterValues}
            />
            <TableItemSelectButton
              allTableItems={allTableItems}
              selectedTableItems={selectedTableItems}
              onApplyClick={onApplyClick}
            />
          </HStack>
        </Flex>
        <Table>
          <TableHeaderRow>
            <TableHeaderCheckboxColumn
              isChecked={isAllChecked}
              onChange={handleAllCheckboxClick}
            />
            <TableHeaderColumn field={"NAME"} onSortClick={handleSortClick}>
              ポップアップ名
            </TableHeaderColumn>
            <TableHeaderColumn>実施ステータス</TableHeaderColumn>
            {role !== "SAAS_CLIENT" && (
              <TableHeaderColumn>承認状態</TableHeaderColumn>
            )}
            <TableHeaderColumn>画像</TableHeaderColumn>
            {selectedTableItems.map((item) =>
              item.orderField ? (
                <TableHeaderColumn
                  key={item.label}
                  field={item.orderField}
                  onSortClick={handleSortClick}
                >
                  {item.label}
                </TableHeaderColumn>
              ) : (
                <TableHeaderColumn key={item.label}>
                  {item.label}
                </TableHeaderColumn>
              )
            )}
            <TableHeaderColumn>ポップアップグループID</TableHeaderColumn>
            <TableHeaderColumn>アクション</TableHeaderColumn>
          </TableHeaderRow>
          {popups.map((popup) => (
            <PopupTableDataRow
              key={popup.slug}
              popupRef={popup}
              siteSlug={site.slug}
              dataColumns={dataColumns}
              isChecked={selectedSlugs.includes(popup.slug)}
              onCheck={handleCheckboxClick}
              connectionId={site.popupGroups.__id}
              role={role}
            />
          ))}
        </Table>
        <TablePagination {...tablePaginationProps} />
        <PopupCsvUploadModal
          isOpen={isCsvImportOpen}
          onClose={onCsvImportClose}
          siteSlug={site.slug}
        />
      </Box>
    </PageLayout>
  );
};
